import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import useSearchParams from '@hooks/useSearchParams';
import TransactionsByStatus from './TransactionsByStatus';
import * as S from './styles';
import TransactionsHistory from './TransactionHistory';
import TransactionStatus from './TransactionStatus';
import AverageTransactionTime from './AverageTransactionTime';
import TransactionsByType from './TransactionsByType';

export interface IOption {
  label: string;
  value: string;
}

function TransactionsDashboard() {
  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    extraParams: [
      'lastTimeAverage',
      'groupByTimeAverage',
      'lastTimeByStatus',
      'groupByTimeBystatus',
      'lastTimeHistory',
      'groupByTimeHistory',
      'lastTimeStatus',
      'groupByTimeStatus',
      'lastTimeType',
      'groupByTimeType',
    ],
  });

  const { t } = useTranslation(['date-picker']);

  const defaultOptions = useMemo(
    () => ({
      lastTimeOptions: [
        { label: t('last 5 minutes', { minutes: 5 }), value: '5m' },
        { label: t('last 10 minutes', { minutes: 10 }), value: '10m' },
        { label: t('last 15 minutes', { minutes: 15 }), value: '15m' },
        { label: t('last 30 minutes', { minutes: 30 }), value: '30m' },
        { label: t('last 1 hour', { days: 1 }), value: '1h' },
        { label: t('last 3 hour', { days: 3 }), value: '3h' },
        { label: t('last 24 hour', { hours: 24 }), value: '24h' },
        { label: t('last-week', { days: 7 }), value: '7d' },
        { label: t('last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('last-month', { days: 30 }), value: '30d' },
        { label: t('last-two-months', { days: 60 }), value: '60d' },
        { label: t('last-three-months', { days: 90 }), value: '90d' },
      ],
      groupedOptions: [
        { label: t('grouped.last-5-minutes', { minutes: 5 }), value: '5m' },
        { label: t('grouped.last-10-minutes', { minutes: 10 }), value: '10m' },
        { label: t('grouped.last-15-minutes', { minutes: 15 }), value: '15m' },
        { label: t('grouped.last-30-minutes', { minutes: 30 }), value: '30m' },
        { label: t('grouped.last-1-hour', { days: 1 }), value: '1h' },
        { label: t('grouped.last-3-hour', { days: 3 }), value: '3h' },
        { label: t('grouped.last-24-hour', { hours: 24 }), value: '24h' },
        { label: t('grouped.last-week', { days: 7 }), value: '7d' },
        { label: t('grouped.last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('grouped.last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('grouped.last-month', { days: 30 }), value: '30d' },
        { label: t('grouped.last-two-months', { days: 60 }), value: '60d' },
        { label: t('grouped.last-three-months', { days: 90 }), value: '90d' },
      ],
    }),
    [t],
  );

  const optionsWithout5min = useMemo(
    () => ({
      lastTimeOptions: [
        { label: t('last 10 minutes', { minutes: 10 }), value: '10m' },
        { label: t('last 15 minutes', { minutes: 15 }), value: '15m' },
        { label: t('last 30 minutes', { minutes: 30 }), value: '30m' },
        { label: t('last 1 hour', { days: 1 }), value: '1h' },
        { label: t('last 3 hour', { days: 3 }), value: '3h' },
        { label: t('last 24 hour', { hours: 24 }), value: '24h' },
        { label: t('last-week', { days: 7 }), value: '7d' },
        { label: t('last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('last-month', { days: 30 }), value: '30d' },
        { label: t('last-two-months', { days: 60 }), value: '60d' },
        { label: t('last-three-months', { days: 90 }), value: '90d' },
      ],
      groupedOptions: [
        { label: t('grouped.last-10-minutes', { minutes: 10 }), value: '10m' },
        { label: t('grouped.last-15-minutes', { minutes: 15 }), value: '15m' },
        { label: t('grouped.last-30-minutes', { minutes: 30 }), value: '30m' },
        { label: t('grouped.last-1-hour', { days: 1 }), value: '1h' },
        { label: t('grouped.last-3-hour', { days: 3 }), value: '3h' },
        { label: t('grouped.last-24-hour', { hours: 24 }), value: '24h' },
        { label: t('grouped.last-week', { days: 7 }), value: '7d' },
        { label: t('grouped.last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('grouped.last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('grouped.last-month', { days: 30 }), value: '30d' },
        { label: t('grouped.last-two-months', { days: 60 }), value: '60d' },
        { label: t('grouped.last-three-months', { days: 90 }), value: '90d' },
      ],
    }),
    [t],
  );
  const getOptionsValue = (
    searchParam: string,
    defaultValue: string,
    options: { lastTimeOptions: IOption[] },
  ) => {
    const isValidValue = options?.lastTimeOptions?.find(
      option => option?.value === searchParam,
    );

    if (!isValidValue && !!searchParam) {
      clearSearchParams();
      return defaultValue;
    }

    return isValidValue ? searchParam : defaultValue;
  };

  return (
    <S.DashContainer>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <AverageTransactionTime
            options={defaultOptions}
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            getOptionsValue={
              (searchParam: string, defaultValue: string) =>
                getOptionsValue(searchParam, defaultValue, defaultOptions)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Col>
        <Col xs={24}>
          <TransactionsByStatus
            options={defaultOptions}
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            getOptionsValue={
              (searchParam: string, defaultValue: string) =>
                getOptionsValue(searchParam, defaultValue, defaultOptions)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Col>
        <Col xs={24}>
          <TransactionsHistory
            options={optionsWithout5min}
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            getOptionsValue={
              (searchParam: string, defaultValue: string) =>
                getOptionsValue(searchParam, defaultValue, optionsWithout5min)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Col>
        <Col xs={24}>
          <TransactionStatus
            options={optionsWithout5min}
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            getOptionsValue={
              (searchParam: string, defaultValue: string) =>
                getOptionsValue(searchParam, defaultValue, optionsWithout5min)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Col>
        <Col xs={24}>
          <TransactionsByType
            options={optionsWithout5min}
            setSearchParams={setSearchParams}
            searchParams={searchParams}
            getOptionsValue={
              (searchParam: string, defaultValue: string) =>
                getOptionsValue(searchParam, defaultValue, optionsWithout5min)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Col>
      </Row>
    </S.DashContainer>
  );
}

export default TransactionsDashboard;
